import React, { useState } from 'react';
import styled from 'styled-components';
import { FiPlay, FiClock, FiChevronRight, FiCalendar, FiExternalLink } from 'react-icons/fi';
import { utcToChicago } from '../../utils/datetime-comprehensive';
import SharePointVideoPlayer from '../SharePointVideoPlayer';
import Modal from '../Modal';
import { toEmbedUrl } from '../../utils/normalizeSharePointUrl';

const PlaylistContainer = styled.div`
  background: var(--background-secondary);
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  width: 100%;
  box-sizing: border-box;
  max-width: 100%;
  
  @media (max-width: 768px) {
    margin: 0;
    border-radius: 0;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
    width: 100%;
    max-width: 100%;
  }
`;


const WeekSection = styled.div`
  border-bottom: 1px solid var(--border-color);
  
  &:last-child {
    border-bottom: none;
  }
`;

const WeekHeader = styled.div`
  padding: 1rem 1.5rem;
  background: var(--background-primary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: background-color 0.2s ease;
  min-width: 0;
  
  &:hover {
    background: var(--background-hover);
  }
`;

const WeekInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  min-width: 0;
`;

const WeekIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 999px;
  background: var(--primary-color);
  color: var(--text-inverse);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.9rem;
  flex-shrink: 0;
`;

const WeekDetails = styled.div`
  flex: 1;
`;

const WeekTitle = styled.h3`
  margin: 0 0 0.25rem 0;
  color: var(--text-primary);
  font-size: 1.1rem;
  font-weight: 500;
`;

const WeekMeta = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.375rem;
  color: var(--text-muted);
  font-size: 0.85rem;
  
  @media (min-width: 640px) {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
  }
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  min-width: 0;
  overflow-wrap: anywhere;
  word-break: break-word;
`;

const ChevronIcon = styled.div`
  color: var(--text-muted);
  transition: transform 0.2s ease;
  transform: ${props => props.$expanded ? 'rotate(90deg)' : 'rotate(0deg)'};
`;

const WeekContent = styled.div`
  max-height: ${props => props.$expanded ? '1000px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease;
`;

const VideoItem = styled.div`
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--border-color);
  cursor: pointer;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  gap: 1rem;
  min-height: 60px; /* Ensure minimum touch target height */
  width: 100%;
  box-sizing: border-box;
  max-width: 100%;
  overflow: hidden;
  
  &:hover {
    background: var(--background-hover);
  }
  
  &:last-child {
    border-bottom: none;
  }
  
  &.active {
    background: var(--primary-light);
    border-left: 4px solid var(--primary-color);
  }
  
  @media (max-width: 768px) {
    padding: 12px 16px;
    gap: 12px;
    min-height: 56px;
    flex-wrap: wrap;
  }
  
  @media (max-width: 480px) {
    padding: 12px 12px;
    gap: 10px;
    min-height: 56px;
  }
`;

const VideoThumbnail = styled.div`
  width: 60px;
  height: 40px;
  background: var(--background-primary);
  border-radius: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-muted);
  border: 1px solid var(--border-color);
  position: relative;
  flex-shrink: 0; /* Prevent shrinking on mobile */
  
  @media (max-width: 480px) {
    width: 48px;
    height: 36px;
  }
`;

const SharePointIndicator = styled.div`
  position: absolute;
  top: -4px;
  right: -4px;
  background: var(--info-color);
  color: var(--text-inverse);
  border-radius: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  border: 2px solid var(--background-secondary);
  z-index: 1; /* Ensure it's above other elements */
  
  @media (max-width: 480px) {
    width: 18px;
    height: 18px;
    font-size: 9px;
    top: -3px;
    right: -3px;
  }
`;

const VideoInfo = styled.div`
  flex: 1 1 0;
  min-width: 0;
  max-width: calc(100% - 80px); /* Account for thumbnail width + gap */
  overflow: hidden; /* Prevent text overflow */
  
  @media (max-width: 480px) {
    max-width: calc(100% - 68px); /* Account for smaller thumbnail + gap */
  }
`;

const VideoTitle = styled.h4`
  margin: 0 0 0.25rem 0;
  color: var(--text-primary);
  font-size: 0.95rem;
  font-weight: 500;
  min-width: 0;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  max-width: 100%;
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
    line-height: 1.2;
  }
`;

const VideoDescription = styled.p`
  margin: 0;
  color: var(--text-muted);
  font-size: 0.8rem;
  line-height: 1.3;
  overflow-wrap: anywhere;
  word-break: break-word;
  min-width: 0;
`;

// duration styling removed (no longer used)

const EmptyState = styled.div`
  padding: 3rem 1.5rem;
  text-align: center;
  color: var(--text-muted);
`;

const EmptyIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
`;

function VideoPlaylist({ recordings, onVideoSelect, selectedVideo }) {
  const [expandedWeeks, setExpandedWeeks] = useState(new Set());
  const [sharePointModalOpen, setSharePointModalOpen] = useState(false);
  const [activeSharePointVideo, setActiveSharePointVideo] = useState(null);

  // Group recordings by week
  const recordingsByWeek = recordings.reduce((acc, recording) => {
    const week = recording.week || 1;
    if (!acc[week]) {
      acc[week] = [];
    }
    acc[week].push(recording);
    return acc;
  }, {});

  // Sort weeks and recordings within each week
  const sortedWeeks = Object.keys(recordingsByWeek)
    .map(Number)
    .sort((a, b) => a - b);

  sortedWeeks.forEach(week => {
    recordingsByWeek[week].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  });

  const toggleWeek = (week) => {
    const newExpanded = new Set(expandedWeeks);
    if (newExpanded.has(week)) {
      newExpanded.delete(week);
    } else {
      newExpanded.add(week);
    }
    setExpandedWeeks(newExpanded);
  };

  const handleVideoClick = (recording) => {
    // Check if this recording has SharePoint links
    if (recording.spEmbedUrl) {
      setActiveSharePointVideo(recording);
      setSharePointModalOpen(true);
    } else if (onVideoSelect) {
      onVideoSelect(recording);
    }
  };


  const closeSharePointModal = () => {
    setSharePointModalOpen(false);
    setActiveSharePointVideo(null);
  };

  if (recordings.length === 0) {
    return (
      <EmptyState>
        <EmptyIcon>🎥</EmptyIcon>
        <h3>No recordings available</h3>
        <p>Recordings will appear here once they are added to the course.</p>
      </EmptyState>
    );
  }

  return (
    <>
      <PlaylistContainer>

      {sortedWeeks.map(week => {
        const weekRecordings = recordingsByWeek[week];
        const isExpanded = expandedWeeks.has(week);
        const totalDuration = weekRecordings.reduce((total, recording) => {
          // Simple duration parsing - could be enhanced
          if (recording.duration) {
            const parts = recording.duration.split(':');
            if (parts.length === 3) {
              return total + (parseInt(parts[0]) * 3600) + (parseInt(parts[1]) * 60) + parseInt(parts[2]);
            }
          }
          return total;
        }, 0);

        const formatDuration = (seconds) => {
          const hours = Math.floor(seconds / 3600);
          const minutes = Math.floor((seconds % 3600) / 60);
          if (hours > 0) {
            return `${hours}h ${minutes}m`;
          }
          return `${minutes}m`;
        };

        const formatChicagoDate = (iso) => {
          try {
            if (!iso) return '';
            // Prefer the date portion only; treat it as the intended wall-date
            const datePart = typeof iso === 'string' ? iso.slice(0, 10) : '';
            if (datePart) {
              const parts = datePart.split('-');
              const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
              return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
            }
            const dt = utcToChicago(iso);
            // Render date only (no time)
            return dt.toFormat('MMM d, yyyy');
          } catch (e) {
            return '';
          }
        };

        return (
          <WeekSection key={week}>
            <WeekHeader onClick={() => toggleWeek(week)}>
              <WeekInfo>
                <WeekIcon>W{week}</WeekIcon>
                <WeekDetails>
                  <WeekTitle>Week {week}</WeekTitle>
                  <WeekMeta>
                    <MetaItem>
                      <FiPlay size={14} />
                      {weekRecordings.length} recording{weekRecordings.length !== 1 ? 's' : ''}
                    </MetaItem>
                    <MetaItem>
                      <FiClock size={14} />
                      {totalDuration > 0 ? formatDuration(totalDuration) : '0m'}
                    </MetaItem>
                    <MetaItem>
                      <FiCalendar size={14} />
                      {weekRecordings[0]?.recording_date ? formatChicagoDate(weekRecordings[0].recording_date) : 'No date'}
                    </MetaItem>
                  </WeekMeta>
                </WeekDetails>
              </WeekInfo>
              <ChevronIcon $expanded={isExpanded}>
                <FiChevronRight size={20} />
              </ChevronIcon>
            </WeekHeader>

            <WeekContent $expanded={isExpanded}>
              {weekRecordings.map((recording, index) => (
                <VideoItem
                  key={recording.id}
                  className={selectedVideo?.id === recording.id ? 'active' : ''}
                  onClick={() => handleVideoClick(recording)}
                >
                  <VideoThumbnail>
                    <FiPlay size={20} />
                    {recording.spEmbedUrl && (
                      <SharePointIndicator title="SharePoint Video">
                        <FiExternalLink size={10} />
                      </SharePointIndicator>
                    )}
                  </VideoThumbnail>
                  <VideoInfo>
                    <VideoTitle>{recording.title}</VideoTitle>
                    {recording.description && recording.description.trim() && recording.description.trim() !== '<p></p>' && (
                      <VideoDescription>{recording.description}</VideoDescription>
                    )}
                  </VideoInfo>
                  {/* Duration removed per design request */}
                </VideoItem>
              ))}
            </WeekContent>
          </WeekSection>
        );
      })}
    </PlaylistContainer>

    {/* SharePoint Video Modal */}
    <Modal 
      open={sharePointModalOpen} 
      onClose={closeSharePointModal} 
      title={activeSharePointVideo?.title || "Course Recording"}
    >
      {activeSharePointVideo && (
        <SharePointVideoPlayer
          spEmbedUrl={toEmbedUrl(activeSharePointVideo.spEmbedUrl)}
          title={activeSharePointVideo.title}
        />
      )}
    </Modal>
  </>
  );
}

export default VideoPlaylist;
